import {
  IonMenu,
  IonHeader,
  IonToolbar,
  IonMenuButton,
  IonButtons,
  IonRow,
  IonLabel,
  IonContent,
  IonFooter,
} from '@ionic/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './Menu.component.css'
import { useDispatch, useSelector } from 'react-redux'
import OfflineIndicator from '../offlineIndicator/OfflineIndicator.component'
import { getUserSelector } from '../../features/user/selectors'
import React from 'react'
import { useOfflineStatus } from '../../utilities/isOnline.util'
import { getReleaseVersionForMenu } from '../../utilities/version.util'
import ZollSupportTicketModal from '../../features/zollSupportTicket/component/ZollSupportTicketModal.component'
import { ISegmentButton } from '../segmentButtons/SegmentButtons.component'
import { TabSegmentButtons } from '../segmentButtons/tabSegmentButtons/TabSegmentButtons.component'
import NotificationCenter from '../../features/notificationCenter/component/NotificationCenter.component'
import PcrMenuContent from './content/PcrMenuContent.content'
import {
  fetchNotifications,
  markNotificationsViewed,
} from '../../features/notificationCenter/store/notificationCenter.actions'
import { getNotificationCenterSelector, hasNewNotificationsSelector } from '../../features/notificationCenter/selectors'

enum MenuTab {
  Pcr = 1,
  NotificationCenter = 2,
}

const MenuComponent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(getUserSelector)
  const { authState } = user
  const isSwipeEnable = authState ? true : false
  const menuRef = useRef<HTMLIonMenuElement>(null)
  const isOfflineStatus = useOfflineStatus()
  const { polledNotificationIds } = useSelector(getNotificationCenterSelector)
  const hasNewNotifications = useSelector(hasNewNotificationsSelector)
  const [showModal, setShowModal] = useState(false)
  const [activeMenuTabId, setActiveMenuTabId] = useState<number>(MenuTab.Pcr)

  const effectiveTabId = !isOfflineStatus ? activeMenuTabId : MenuTab.Pcr

  const menuSegments: ISegmentButton[] = [
    { id: MenuTab.Pcr, name: t('common.menuTabs.main') },
    { id: MenuTab.NotificationCenter, name: t('common.menuTabs.notificationCenterTab'), badge: hasNewNotifications },
  ]
  const selectedMenuTab = menuSegments.find((segment) => segment.id === effectiveTabId) ?? menuSegments[0]

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      setActiveMenuTabId(1)
      menuRef.current?.close()
    }
  }, [authState])

  const closeMenu = () => {
    setActiveMenuTabId(MenuTab.Pcr)
    menuRef.current?.close()
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const getVersionLabel = () => {
    if (process.env.NODE_ENV === 'development') {
      return <div className='text-align'>{t('login.version.versionRunningLocally')}</div>
    }
    return (
      <div className='text-align'>{getReleaseVersionForMenu(t('login.version.version'), t('login.version.build'))}</div>
    )
  }

  return (
    <IonMenu
      className='menu-animation menu-content'
      ref={menuRef}
      contentId='main-now-content'
      side='end'
      swipe-gesture={isSwipeEnable}
      type='overlay'
      onIonDidClose={() => setActiveMenuTabId(MenuTab.Pcr)}
      onIonDidOpen={() => !isOfflineStatus && dispatch(fetchNotifications())}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='end'>
            <OfflineIndicator />
            <IonMenuButton color='primary-alt'></IonMenuButton>
          </IonButtons>
          <IonRow className='ion-justify-content-center'>
            <IonLabel className='ion-margin-end'>{t('common.emsChartsNOW')}</IonLabel>
          </IonRow>
        </IonToolbar>
      </IonHeader>
      <IonContent className='ion-padding'>
        {!isOfflineStatus && (
          <div className='menu-tabs'>
            <TabSegmentButtons
              dataTestId='MenuTabs'
              className='tabs-segment-full-width'
              segmentsList={menuSegments}
              selected={selectedMenuTab}
              onChangeAction={(segment) => {
                setActiveMenuTabId(segment.id)
                if (segment.id === MenuTab.NotificationCenter) {
                  dispatch(markNotificationsViewed(polledNotificationIds))
                }
              }}
            />
          </div>
        )}
        {effectiveTabId === MenuTab.NotificationCenter ? (
          <div className='menu-tab-panel-notifications'>
            <NotificationCenter onCloseMenu={closeMenu} />
          </div>
        ) : (
          <PcrMenuContent onCloseMenu={closeMenu} onOpenSupportTicket={() => setShowModal(true)} />
        )}
      </IonContent>
      <ZollSupportTicketModal showModal={showModal} resetModal={closeModal} />
      <IonFooter className='footer-safe-area'>
        <IonRow className='ion-padding'>{getVersionLabel()} </IonRow>
      </IonFooter>
    </IonMenu>
  )
}

export default MenuComponent
